import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CellData } from "./excel-parse";

/**
 * Excel-like rectangular cell selection for {@link ExcelViewer}.
 *
 * Coordinates are `{ r, c }` where `r` is the row index into `sheetData` and
 * `c` is the ORDINAL into `visibleColumnIndices` (not the physical column
 * index). Working in visible ordinals means hidden columns are excluded and the
 * selected rectangle is always contiguous on screen. The physical column of an
 * ordinal is `visibleColumnIndices[c]`.
 *
 * The selection is a rectangle spanning `anchor`→`focus`. Click sets both;
 * drag / shift-click moves `focus`. Row/column-header clicks select a whole
 * band. Stats (sum / average / count) are derived from the cells' raw
 * `numericValue` — see `excel-parse.ts`.
 */

interface CellRef {
  r: number;
  c: number;
}

export interface SelectionStats {
  /** Total cells covered by the rectangle. */
  cellCount: number;
  /** How many of those cells hold a numeric value. */
  numericCount: number;
  /** Sum of the numeric cells. */
  sum: number;
  /** Mean of the numeric cells (0 when none). */
  average: number;
}

interface UseCellSelectionArgs {
  sheetData: CellData[][];
  visibleColumnIndices: number[];
  /**
   * Changing this clears the selection — pass a value that differs whenever the
   * rendered content swaps (active sheet, file↔slice buffer) so a stale
   * rectangle never lingers over unrelated data.
   */
  resetKey: string;
}

export function useCellSelection({
  sheetData,
  visibleColumnIndices,
  resetKey,
}: UseCellSelectionArgs) {
  const [anchor, setAnchor] = useState<CellRef | null>(null);
  const [focus, setFocus] = useState<CellRef | null>(null);
  const draggingRef = useRef(false);

  const rowCount = sheetData.length;
  const colCount = visibleColumnIndices.length;
  const lastRow = Math.max(0, rowCount - 1);
  const lastCol = Math.max(0, colCount - 1);

  const clear = useCallback(() => {
    setAnchor(null);
    setFocus(null);
    draggingRef.current = false;
  }, []);

  // Clear when the rendered content swaps (sheet / buffer).
  useEffect(() => {
    clear();
  }, [resetKey, clear]);

  // A drag ends on the next mouseup anywhere. Registered once; the ref gate
  // keeps it cheap when no drag is in progress.
  useEffect(() => {
    const onUp = () => {
      draggingRef.current = false;
    };
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
  }, []);

  const beginSelect = useCallback(
    (r: number, c: number, shiftKey: boolean) => {
      draggingRef.current = true;
      if (shiftKey && anchor) {
        setFocus({ r, c });
      } else {
        setAnchor({ r, c });
        setFocus({ r, c });
      }
    },
    [anchor],
  );

  const extendTo = useCallback((r: number, c: number) => {
    if (!draggingRef.current) return;
    setFocus({ r, c });
  }, []);

  const selectRow = useCallback(
    (r: number, shiftKey: boolean) => {
      draggingRef.current = false;
      if (shiftKey && anchor) {
        setFocus({ r, c: lastCol });
      } else {
        setAnchor({ r, c: 0 });
        setFocus({ r, c: lastCol });
      }
    },
    [anchor, lastCol],
  );

  const selectColumn = useCallback(
    (c: number, shiftKey: boolean) => {
      draggingRef.current = false;
      if (shiftKey && anchor) {
        setFocus({ r: lastRow, c });
      } else {
        setAnchor({ r: 0, c });
        setFocus({ r: lastRow, c });
      }
    },
    [anchor, lastRow],
  );

  const rect = useMemo(() => {
    if (!anchor || !focus) return null;
    return {
      minR: Math.min(anchor.r, focus.r),
      maxR: Math.max(anchor.r, focus.r),
      minC: Math.min(anchor.c, focus.c),
      maxC: Math.max(anchor.c, focus.c),
    };
  }, [anchor, focus]);

  const isSelected = useCallback(
    (r: number, c: number) =>
      rect != null &&
      r >= rect.minR &&
      r <= rect.maxR &&
      c >= rect.minC &&
      c <= rect.maxC,
    [rect],
  );

  const isActive = useCallback(
    (r: number, c: number) => anchor?.r === r && anchor?.c === c,
    [anchor],
  );

  const stats = useMemo<SelectionStats>(() => {
    if (!rect) return { cellCount: 0, numericCount: 0, sum: 0, average: 0 };
    let cellCount = 0;
    let numericCount = 0;
    let sum = 0;
    for (let r = rect.minR; r <= rect.maxR; r++) {
      const row = sheetData[r];
      if (!row) continue;
      for (let c = rect.minC; c <= rect.maxC; c++) {
        // A column hidden after the selection was made shrinks
        // `visibleColumnIndices`, leaving the stored ordinal `c` pointing past
        // its end. Skip those ghost ordinals so they neither inflate the count
        // nor depress the average.
        const physicalCol = visibleColumnIndices[c];
        if (physicalCol === undefined) continue;
        cellCount++;
        const cell = row[physicalCol];
        if (cell && typeof cell.numericValue === "number") {
          sum += cell.numericValue;
          numericCount++;
        }
      }
    }
    return {
      cellCount,
      numericCount,
      sum,
      average: numericCount > 0 ? sum / numericCount : 0,
    };
  }, [rect, sheetData, visibleColumnIndices]);

  return {
    beginSelect,
    extendTo,
    selectRow,
    selectColumn,
    isSelected,
    isActive,
    clear,
    stats,
    hasSelection: rect != null,
  };
}
